import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, CheckCircle, AlertCircle, ChevronRight } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useWorkspaceStore } from '../../stores/workspace.js';
import { useQueryClient } from '@tanstack/react-query';

type EntityType = 'company' | 'contact';
type Step = 'upload' | 'mapping' | 'results';

interface ImportField {
  key: string;
  label: string;
  required: boolean;
}

interface FieldMapping {
  csvColumn: string;
  targetField: string | null;
}

interface ParseResult {
  headers: string[];
  preview: Record<string, string>[];
  totalRows: number;
}

interface ImportRowResult {
  rowNumber: number;
  success: boolean;
  id?: string;
  errors: string[];
}

interface ImportResult {
  imported: number;
  skipped: number;
  errors: ImportRowResult[];
}

interface Props {
  entityType: EntityType;
  onClose: () => void;
}

const API_URL = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '';

export default function CsvImportModal({ entityType, onClose }: Props) {
  const { t } = useTranslation('dataManagement');
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [importFields, setImportFields] = useState<ImportField[]>([]);
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [parseError, setParseError] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  async function handleFileSelect(f: File) {
    setFile(f);
    setParseError('');
    setIsParsing(true);

    try {
      const token = await getToken();
      const fd = new FormData();
      fd.append('file', f);

      const [parseResp, fieldsResp] = await Promise.all([
        fetch(`${API_URL}/v1/data/parse-csv`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Workspace-Id': workspaceId ?? '',
          },
          body: fd,
        }),
        fetch(`${API_URL}/v1/data/import-fields/${entityType}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Workspace-Id': workspaceId ?? '',
          },
        }),
      ]);

      if (!parseResp.ok) {
        const body = await parseResp.json() as { error?: { message?: string } };
        throw new Error(body.error?.message ?? 'Failed to parse CSV');
      }
      if (!fieldsResp.ok) throw new Error('Failed to load import fields');

      const parseData = await parseResp.json() as { data: ParseResult };
      const fieldsData = await fieldsResp.json() as { data: ImportField[] };

      setParseResult(parseData.data);
      setImportFields(fieldsData.data);

      // Auto-map by matching header names (case-insensitive)
      const autoMappings: FieldMapping[] = parseData.data.headers.map((h) => {
        const match = fieldsData.data.find(
          (f) => f.key.toLowerCase() === h.toLowerCase() ||
                 f.label.toLowerCase() === h.toLowerCase(),
        );
        return { csvColumn: h, targetField: match?.key ?? null };
      });
      setMappings(autoMappings);
      setStep('mapping');
    } catch (err) {
      setParseError(err instanceof Error ? err.message : t('upload.error_generic'));
    } finally {
      setIsParsing(false);
    }
  }

  async function handleImport() {
    if (!file || !parseResult) return;
    setIsImporting(true);

    try {
      const token = await getToken();
      const fd = new FormData();
      fd.append('file', file);
      fd.append('mappings', JSON.stringify(mappings));

      const resp = await fetch(`${API_URL}/v1/data/import/${entityType}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Workspace-Id': workspaceId ?? '',
        },
        body: fd,
      });

      if (!resp.ok) {
        const body = await resp.json() as { error?: { message?: string } };
        throw new Error(body.error?.message ?? 'Import failed');
      }

      const data = await resp.json() as { data: ImportResult };
      setImportResult(data.data);
      setStep('results');

      // Invalidate the list query so the page refreshes
      const queryKey = entityType === 'company' ? 'companies' : 'contacts';
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
    } catch (err) {
      setParseError(err instanceof Error ? err.message : t('upload.error_generic'));
    } finally {
      setIsImporting(false);
    }
  }

  function setMapping(csvColumn: string, targetField: string | null) {
    setMappings((prev) =>
      prev.map((m) => m.csvColumn === csvColumn ? { ...m, targetField } : m),
    );
  }

  const entityLabel = entityType === 'company' ? t('entity.company') : t('entity.contact');
  const hasRequiredMappings = importFields
    .filter((f) => f.required)
    .every((f) => mappings.some((m) => m.targetField === f.key));

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        zIndex: 'var(--z-modal)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 'var(--space-4)',
      }}
    >
      <div style={{
        background: 'var(--color-bg)', borderRadius: 'var(--radius-xl)',
        width: '100%', maxWidth: 680, maxHeight: '90dvh', overflow: 'auto',
        boxShadow: 'var(--shadow-xl)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--space-5) var(--space-6)', borderBottom: '1px solid var(--color-border)',
          position: 'sticky', top: 0, background: 'var(--color-bg)', zIndex: 1,
        }}>
          <div>
            <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
              {t('modal.title', { entity: entityLabel })}
            </h2>
            {/* Step indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginTop: 4 }}>
              {(['upload', 'mapping', 'results'] as Step[]).map((s, idx) => (
                <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {idx > 0 && <ChevronRight size={12} style={{ color: 'var(--color-text-muted)' }} />}
                  <span style={{
                    fontSize: 'var(--text-xs)',
                    color: step === s ? 'var(--color-brand)' : 'var(--color-text-muted)',
                    fontWeight: step === s ? 'var(--font-medium)' : undefined,
                  }}>
                    {t(`steps.${s}`)}
                  </span>
                </span>
              ))}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-400)', display: 'flex' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: 'var(--space-6)' }}>
          {/* ── Step 1: Upload ── */}
          {step === 'upload' && (
            <div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-5)' }}>
                {t('upload.instructions', { entity: entityLabel })}
              </p>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleFileSelect(f);
                }}
              />

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isParsing}
                style={{
                  width: '100%', padding: 'var(--space-10)',
                  border: '2px dashed var(--color-border)', borderRadius: 'var(--radius-lg)',
                  background: 'var(--color-bg-subtle)', cursor: isParsing ? 'not-allowed' : 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-3)',
                }}
              >
                <Upload size={32} style={{ color: 'var(--color-text-muted)' }} />
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                  {isParsing ? t('upload.parsing') : t('upload.drop_prompt')}
                </span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                  {t('upload.format_hint')}
                </span>
              </button>

              {parseError && (
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-danger)', marginTop: 'var(--space-3)' }}>
                  {parseError}
                </p>
              )}

              <div style={{ marginTop: 'var(--space-4)', padding: 'var(--space-4)', background: 'var(--color-bg-subtle)', borderRadius: 'var(--radius-md)' }}>
                <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-2)' }}>
                  {t('upload.expected_columns')}
                </p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono)' }}>
                  {entityType === 'company'
                    ? 'name, type, website, phone, email, address_line1, city, state, zip, country, notes'
                    : 'firstName, lastName, title, email, phone, mobile, notes'}
                </p>
              </div>
            </div>
          )}

          {/* ── Step 2: Field Mapping ── */}
          {step === 'mapping' && parseResult && (
            <div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-2)' }}>
                {t('mapping.instructions', { count: parseResult.totalRows })}
              </p>

              {/* Preview table */}
              <div style={{
                overflowX: 'auto', marginBottom: 'var(--space-5)',
                border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-xs)' }}>
                  <thead>
                    <tr style={{ background: 'var(--color-bg-subtle)' }}>
                      {parseResult.headers.map((h) => (
                        <th key={h} style={{ padding: 'var(--space-2) var(--space-3)', textAlign: 'left', whiteSpace: 'nowrap', fontWeight: 'var(--font-medium)', color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parseResult.preview.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                        {parseResult.headers.map((h) => (
                          <td key={h} style={{ padding: 'var(--space-2) var(--space-3)', color: 'var(--color-text-muted)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row[h] ?? ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mapping selectors */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
                <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                  {t('mapping.map_columns')}
                </p>
                {mappings.map((m) => (
                  <div key={m.csvColumn} style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <div style={{
                      padding: 'var(--space-2) var(--space-3)',
                      background: 'var(--color-bg-subtle)', border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
                      color: 'var(--color-text)', fontFamily: 'var(--font-mono)',
                    }}>
                      {m.csvColumn}
                    </div>
                    <ChevronRight size={16} style={{ color: 'var(--color-text-muted)' }} />
                    <select
                      value={m.targetField ?? ''}
                      onChange={(e) => setMapping(m.csvColumn, e.target.value || null)}
                      style={{
                        padding: 'var(--space-2) var(--space-3)',
                        border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--text-sm)', color: 'var(--color-text)',
                        background: 'var(--color-bg)', appearance: 'auto',
                      }}
                    >
                      <option value="">{t('mapping.skip_column')}</option>
                      {importFields.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}{f.required ? ' *' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {parseError && (
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-danger)', marginBottom: 'var(--space-3)' }}>
                  {parseError}
                </p>
              )}

              <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => { setStep('upload'); setFile(null); setParseResult(null); }}
                  style={cancelBtnStyle}
                >
                  {t('back')}
                </button>
                <button
                  type="button"
                  onClick={() => void handleImport()}
                  disabled={isImporting || !hasRequiredMappings}
                  style={{
                    ...primaryBtnStyle,
                    opacity: (isImporting || !hasRequiredMappings) ? 0.6 : 1,
                    cursor: (isImporting || !hasRequiredMappings) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isImporting ? t('mapping.importing') : t('mapping.import_button', { count: parseResult.totalRows })}
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: Results ── */}
          {step === 'results' && importResult && (
            <div>
              {/* Summary */}
              <div style={{ display: 'flex', gap: 'var(--space-4)', marginBottom: 'var(--space-5)' }}>
                <div style={{
                  flex: 1, padding: 'var(--space-4)', borderRadius: 'var(--radius-lg)',
                  background: 'color-mix(in srgb, var(--color-success) 10%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--color-success) 30%, transparent)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 4 }}>
                    <CheckCircle size={16} style={{ color: 'var(--color-success)' }} />
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-success)' }}>
                      {t('results.imported')}
                    </span>
                  </div>
                  <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                    {importResult.imported}
                  </p>
                </div>
                <div style={{
                  flex: 1, padding: 'var(--space-4)', borderRadius: 'var(--radius-lg)',
                  background: importResult.skipped > 0
                    ? 'color-mix(in srgb, var(--color-danger) 10%, transparent)'
                    : 'var(--color-bg-subtle)',
                  border: `1px solid ${importResult.skipped > 0 ? 'color-mix(in srgb, var(--color-danger) 30%, transparent)' : 'var(--color-border)'}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 4 }}>
                    <AlertCircle size={16} style={{ color: importResult.skipped > 0 ? 'var(--color-danger)' : 'var(--color-text-muted)' }} />
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: importResult.skipped > 0 ? 'var(--color-danger)' : 'var(--color-text-muted)' }}>
                      {t('results.skipped')}
                    </span>
                  </div>
                  <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                    {importResult.skipped}
                  </p>
                </div>
              </div>

              {/* Error rows */}
              {importResult.errors.filter((r) => !r.success).length > 0 && (
                <div>
                  <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-3)' }}>
                    {t('results.error_report')}
                  </p>
                  <div style={{
                    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
                    overflow: 'hidden', maxHeight: 240, overflowY: 'auto',
                  }}>
                    {importResult.errors.filter((r) => !r.success).map((r) => (
                      <div key={r.rowNumber} style={{
                        padding: 'var(--space-2) var(--space-3)',
                        borderBottom: '1px solid var(--color-gray-100)',
                        display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start',
                      }}>
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', flexShrink: 0 }}>
                          {t('results.row_label', { n: r.rowNumber })}
                        </span>
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-danger)' }}>
                          {r.errors.join('; ')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-6)' }}>
                <button type="button" onClick={onClose} style={primaryBtnStyle}>
                  {t('results.done')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const cancelBtnStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-5)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
  color: 'var(--color-text-secondary)', background: 'var(--color-bg)', cursor: 'pointer',
};

const primaryBtnStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-5)', background: 'var(--color-brand)',
  color: 'var(--color-text-inverse)', border: 'none',
  borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
  fontWeight: 'var(--font-medium)', cursor: 'pointer',
};
